import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import Icon from '../Icon';
import { ICONS } from '../icon-map';
import Button from '../Button';
import Popover from '../Popover';
import EmptyState, { Skeleton } from '../EmptyState';
import { renderCellByType } from './cells';
import { useTableScroll } from '../Table/useViewportFit';
import '../ds.css';
import './datatable.css';

/*
 * DataTable: the one table for data in the product.
 *
 *   <DataTable
 *     columns={[
 *       { key: 'contractNo', header: 'Contract', cell: (r) => <span className="font-mono">{r.contractNo}</span> },
 *       { key: 'customer', header: 'Customer' },
 *       { key: 'amount', header: 'Amount', align: 'end', format: 'money' },
 *       { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
 *     ]}
 *     data={rows}
 *     rowKey="id"
 *     exportFileName="contracts"
 *     selectable
 *     bulkActions={(rows, clear) => <Button size="sm" onClick={() => doSomething(rows)}>Close</Button>}
 *   />
 *
 * Every cell and header is centred (owner's rule, numbers too - datatable.css). Rows are compact (40px) by default.
 * Features: search, sortable headers, filters slot, pagination (client or server), row
 * selection with a bulk-action bar, column chooser, density, Excel/CSV export, empty /
 * loading / error states, keyboard-reachable rows.
 */

const getByPath = (row, key) => {
    if (!row || key === undefined || key === null) return undefined;
    if (typeof key !== 'string' || !key.includes('.')) return row[key];
    return key.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), row);
};

const valueOf = (col, row) => {
    if (typeof col.accessor === 'function') return col.accessor(row);
    if (typeof col.accessor === 'string') return getByPath(row, col.accessor);
    return getByPath(row, col.key);
};

const MONEY = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const NUMBER = new Intl.NumberFormat('en-US');

const formatValue = (col, value, row) => {
    if (col.type) return renderCellByType(col.type, value, typeof col.tone === 'function' ? col.tone(row, value) : col.tone);
    if (value === null || value === undefined || value === '') return <span className="ds-cell-empty">—</span>;
    if (col.format === 'money' && Number.isFinite(Number(value))) return MONEY.format(Number(value));
    if (col.format === 'number' && Number.isFinite(Number(value))) return NUMBER.format(Number(value));
    if (value instanceof Date) return value.toLocaleDateString('en-GB');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return String(value);
    return value;
};

const toComparable = (v) => {
    if (v === null || v === undefined || v === '') return null;
    if (v instanceof Date) return v.getTime();
    if (typeof v === 'number') return v;
    if (typeof v === 'boolean') return v ? 1 : 0;
    const s = String(v).trim();
    const n = Number(s.replace(/,/g, ''));
    if (s !== '' && Number.isFinite(n) && /^-?[\d,]*\.?\d+$/.test(s)) return n;
    return s.toLowerCase();
};

const compare = (a, b) => {
    const x = toComparable(a);
    const y = toComparable(b);
    if (x === null && y === null) return 0;
    if (x === null) return 1; // empty values last
    if (y === null) return -1;
    if (typeof x === 'number' && typeof y === 'number') return x - y;
    return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' });
};

const readPrefs = (key) => {
    if (!key) return null;
    try { return JSON.parse(localStorage.getItem(`yelo.table.${key}`)) || null; } catch (e) { return null; }
};

const writePrefs = (key, prefs) => {
    if (!key) return;
    try { localStorage.setItem(`yelo.table.${key}`, JSON.stringify(prefs)); } catch (e) { /* ignore */ }
};

function pageList(page, count) {
    if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
    const set = new Set([1, count, page - 1, page, page + 1]);
    const list = [...set].filter((p) => p >= 1 && p <= count).sort((a, b) => a - b);
    const out = [];
    list.forEach((p, i) => {
        if (i > 0 && p - list[i - 1] > 1) out.push('…');
        out.push(p);
    });
    return out;
}

function SelectBox({ checked, indeterminate, onChange, label }) {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = Boolean(indeterminate) && !checked;
    }, [indeterminate, checked]);
    return (
        <input
            ref={ref}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            onClick={(e) => e.stopPropagation()}
            aria-label={label}
            style={{ width: 15, height: 15, accentColor: 'var(--accent)', cursor: 'pointer' }}
        />
    );
}

export default function DataTable({
    columns = [],
    data = [],
    rowKey = 'id',
    loading = false,
    error = null,

    title,
    description,
    headerActions,
    caption,

    searchable = true,
    searchPlaceholder = 'Search…',
    searchKeys,
    search,
    onSearchChange,

    filters,
    toolbar,
    actions,

    initialSort,
    sort: controlledSort,
    onSortChange,

    pagination = true,
    pageSize: initialPageSize = 25,
    pageSizeOptions = [25, 50, 100],
    serverSide = false,
    total,
    page: controlledPage,
    onPageChange,
    onPageSizeChange,

    selectable = false,
    selectedKeys,
    onSelectionChange,
    bulkActions,

    exportFileName,
    exportFormat = 'xlsx',
    onExport,

    columnToggle = true,
    density: initialDensity = 'default',
    persistKey,

    onRowClick,
    rowClassName,
    rowProps,
    renderExpanded,

    stickyHeader = true,
    maxHeight,
    emptyTitle = 'No records found',
    emptyDescription,
    emptyAction,
    emptyIcon,
    footer,
    className = '',
    card = true,
}) {
    const uid = useId();
    // One scroll per page + a header that stays visible (see useTableScroll)
    const box = useTableScroll({ maxHeight, tailSelector: '.ds-table-card' });
    const prefs = useMemo(() => readPrefs(persistKey), [persistKey]);

    // ---- columns / visibility ----
    const [hidden, setHidden] = useState(() => new Set(
        prefs && Array.isArray(prefs.hidden) ? prefs.hidden : columns.filter((c) => c.hidden).map((c) => c.key)
    ));
    const [density, setDensity] = useState(prefs && prefs.density ? prefs.density : initialDensity);

    useEffect(() => {
        writePrefs(persistKey, { hidden: [...hidden], density });
    }, [persistKey, hidden, density]);

    const visibleColumns = columns.filter((c) => !hidden.has(c.key));

    // ---- search ----
    const [innerSearch, setInnerSearch] = useState('');
    const query = search !== undefined ? search : innerSearch;
    const setQuery = (v) => {
        if (onSearchChange) onSearchChange(v);
        if (search === undefined) setInnerSearch(v);
    };

    // ---- sort ----
    const [innerSort, setInnerSort] = useState(initialSort || null);
    const sortState = controlledSort !== undefined ? controlledSort : innerSort;
    const toggleSort = (key) => {
        let next;
        if (!sortState || sortState.key !== key) next = { key, dir: 'asc' };
        else if (sortState.dir === 'asc') next = { key, dir: 'desc' };
        else next = null;
        if (onSortChange) onSortChange(next);
        if (controlledSort === undefined) setInnerSort(next);
    };

    // ---- rows: search -> sort ----
    const getKey = useCallback((row, index) => {
        if (typeof rowKey === 'function') return rowKey(row, index);
        const k = getByPath(row, rowKey);
        return k !== undefined && k !== null ? k : index;
    }, [rowKey]);

    const processed = useMemo(() => {
        let rows = Array.isArray(data) ? data : [];
        if (!serverSide && query && searchable) {
            const q = String(query).trim().toLowerCase();
            if (q) {
                const cols = searchKeys
                    ? searchKeys.map((k) => ({ key: k }))
                    : columns.filter((c) => c.searchable !== false);
                rows = rows.filter((row) => cols.some((c) => {
                    const v = c.searchValue ? c.searchValue(row) : valueOf(c, row);
                    return v !== null && v !== undefined && String(v).toLowerCase().includes(q);
                }));
            }
        }
        if (!serverSide && sortState) {
            const col = columns.find((c) => c.key === sortState.key);
            if (col) {
                const get = col.sortValue || ((row) => valueOf(col, row));
                const dir = sortState.dir === 'desc' ? -1 : 1;
                rows = rows
                    .map((row, i) => ({ row, i }))
                    .sort((a, b) => (compare(get(a.row), get(b.row)) * dir) || (a.i - b.i))
                    .map((x) => x.row);
            }
        }
        return rows;
    }, [data, query, searchable, searchKeys, columns, sortState, serverSide]);

    // ---- pagination ----
    const [innerPage, setInnerPage] = useState(1);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const page = controlledPage !== undefined ? controlledPage : innerPage;
    const totalRows = serverSide ? (total ?? processed.length) : processed.length;
    const pageCount = pagination ? Math.max(1, Math.ceil(totalRows / pageSize)) : 1;
    const safePage = Math.min(Math.max(1, page), pageCount);

    const setPage = (p) => {
        if (onPageChange) onPageChange(p);
        if (controlledPage === undefined) setInnerPage(p);
    };

    useEffect(() => {
        if (controlledPage === undefined) setInnerPage(1);
    }, [query, sortState, controlledPage, pageSize]);

    const pageRows = useMemo(() => {
        if (!pagination || serverSide) return processed;
        const start = (safePage - 1) * pageSize;
        return processed.slice(start, start + pageSize);
    }, [processed, pagination, serverSide, safePage, pageSize]);

    // ---- selection ----
    const [innerSelected, setInnerSelected] = useState(() => new Set());
    const selected = selectedKeys ? new Set(selectedKeys) : innerSelected;
    const setSelected = (nextSet) => {
        if (onSelectionChange) onSelectionChange([...nextSet]);
        if (!selectedKeys) setInnerSelected(nextSet);
    };
    const processedKeys = useMemo(() => processed.map((r, i) => getKey(r, i)), [processed, getKey]);
    const selectedRows = useMemo(
        () => processed.filter((r, i) => selected.has(getKey(r, i))),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [processed, selectedKeys, innerSelected, getKey]
    );
    const allSelected = processedKeys.length > 0 && processedKeys.every((k) => selected.has(k));
    const someSelected = processedKeys.some((k) => selected.has(k));
    const clearSelection = () => setSelected(new Set());

    // ---- export ----
    const [exporting, setExporting] = useState(false);
    const exportRows = async (rows) => {
        if (onExport) {
            onExport(rows);
            return;
        }
        setExporting(true);
        try {
            const cols = columns.filter((c) => c.exportable !== false && !hidden.has(c.key));
            const sheetRows = rows.map((row) => {
                const out = {};
                cols.forEach((c) => {
                    const header = c.exportHeader || (typeof c.header === 'string' ? c.header : c.key);
                    const v = c.exportValue ? c.exportValue(row) : valueOf(c, row);
                    out[header] = v instanceof Date ? v.toISOString().slice(0, 10) : (v ?? '');
                });
                return out;
            });
            const XLSX = await import('xlsx');
            const ws = XLSX.utils.json_to_sheet(sheetRows);
            const name = `${exportFileName || 'export'}_${new Date().toISOString().slice(0, 10)}`;
            if (exportFormat === 'csv') {
                const csv = XLSX.utils.sheet_to_csv(ws);
                const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${name}.csv`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            } else {
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Data');
                XLSX.writeFile(wb, `${name}.xlsx`);
            }
        } finally {
            setExporting(false);
        }
    };
    const canExport = Boolean(exportFileName || onExport);

    // ---- render helpers ----
    const colSpan = visibleColumns.length + (selectable ? 1 : 0) + (renderExpanded ? 1 : 0);
    const [expanded, setExpanded] = useState(() => new Set());
    const toggleExpanded = (k) => {
        const next = new Set(expanded);
        if (next.has(k)) next.delete(k); else next.add(k);
        setExpanded(next);
    };

    const showToolbar = searchable || filters || toolbar || actions || canExport || columnToggle;
    const firstShown = totalRows === 0 ? 0 : (pagination ? (safePage - 1) * pageSize + 1 : 1);
    const lastShown = pagination ? Math.min(safePage * pageSize, totalRows) : totalRows;

    const table = (
        <div ref={box.ref} className={`ds-table-scroll ${box.pageSticky && stickyHeader ? 'is-page-sticky' : ''}`} style={box.style}>
            <table className={`ds-table ${stickyHeader ? 'ds-table--sticky' : ''} ds-table--${density}`}>
                {caption && <caption className="sr-only">{caption}</caption>}
                <thead>
                    <tr>
                        {renderExpanded && <th className="col-expand"><span className="sr-only">Expand</span></th>}
                        {selectable && (
                            <th className="col-select">
                                <SelectBox
                                    checked={allSelected}
                                    indeterminate={someSelected}
                                    label={allSelected ? 'Clear selection' : 'Select all rows'}
                                    onChange={() => setSelected(allSelected ? new Set() : new Set(processedKeys))}
                                />
                            </th>
                        )}
                        {visibleColumns.map((c) => {
                            const sortable = c.sortable !== false && !c.cellOnly;
                            const active = sortState && sortState.key === c.key;
                            const ariaSort = active ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : (sortable ? 'none' : undefined);
                            return (
                                <th
                                    key={c.key}
                                    scope="col"
                                    aria-sort={ariaSort}
                                    className={`${c.align === 'end' ? 'align-end' : c.align === 'center' ? 'align-center' : ''} ${c.headerClassName || ''}`}
                                    style={{ width: c.width, minWidth: c.minWidth }}
                                >
                                    {sortable ? (
                                        <button type="button" className={`ds-sort ${active ? 'is-active' : ''}`} onClick={() => toggleSort(c.key)}>
                                            {c.header}
                                            <span className="ds-sort__icon">
                                                <Icon glyph={active && sortState.dir === 'desc' ? ICONS.chevronDown : ICONS.chevronUp} size="xs" />
                                            </span>
                                        </button>
                                    ) : c.header}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {loading && pageRows.length === 0 ? (
                        Array.from({ length: 6 }).map((_, i) => (
                            <tr key={`sk-${i}`} className="ds-table__skeleton-row" aria-hidden="true">
                                {Array.from({ length: colSpan }).map((__, j) => (
                                    <td key={j}><Skeleton width={j === 0 ? '60%' : '80%'} height={10} /></td>
                                ))}
                            </tr>
                        ))
                    ) : error ? (
                        <tr className="ds-table__state">
                            <td colSpan={colSpan}>
                                <EmptyState icon={ICONS.alert} title="Could not load data" description={typeof error === 'string' ? error : (error && error.message) || 'Try again in a moment.'} />
                            </td>
                        </tr>
                    ) : pageRows.length === 0 ? (
                        <tr className="ds-table__state">
                            <td colSpan={colSpan}>
                                <EmptyState
                                    icon={emptyIcon || (query ? ICONS.search : ICONS.list)}
                                    title={query ? `No results for "${query}"` : emptyTitle}
                                    description={query ? 'Try a different search or clear the filters.' : emptyDescription}
                                    action={query ? <Button size="sm" onClick={() => setQuery('')}>Clear search</Button> : emptyAction}
                                />
                            </td>
                        </tr>
                    ) : (
                        pageRows.map((row, i) => {
                            const absoluteIndex = pagination && !serverSide ? (safePage - 1) * pageSize + i : i;
                            const k = getKey(row, absoluteIndex);
                            const isSelected = selected.has(k);
                            const isExpanded = expanded.has(k);
                            const extra = rowProps ? rowProps(row, absoluteIndex) || {} : {};
                            const clickable = Boolean(onRowClick);
                            return (
                                <React.Fragment key={k}>
                                    <tr
                                        {...extra}
                                        className={`${isSelected ? 'is-selected' : ''} ${clickable ? 'is-clickable' : ''} ${rowClassName ? rowClassName(row, absoluteIndex) || '' : ''} ${extra.className || ''}`}
                                        tabIndex={clickable ? 0 : undefined}
                                        onClick={clickable ? (e) => onRowClick(row, e) : extra.onClick}
                                        onKeyDown={clickable ? (e) => {
                                            if (e.key === 'Enter' && e.target === e.currentTarget) onRowClick(row, e);
                                        } : extra.onKeyDown}
                                        aria-selected={selectable ? isSelected : undefined}
                                    >
                                        {renderExpanded && (
                                            <td className="col-expand">
                                                <button
                                                    type="button"
                                                    className="yelo-btn yelo-btn--ghost yelo-btn--xs yelo-btn--icon"
                                                    aria-expanded={isExpanded}
                                                    aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                                                    onClick={(e) => { e.stopPropagation(); toggleExpanded(k); }}
                                                >
                                                    <Icon glyph={isExpanded ? ICONS.chevronDown : ICONS.chevronRight} size="xs" />
                                                </button>
                                            </td>
                                        )}
                                        {selectable && (
                                            <td className="col-select">
                                                <SelectBox
                                                    checked={isSelected}
                                                    label="Select row"
                                                    onChange={() => {
                                                        const next = new Set(selected);
                                                        if (next.has(k)) next.delete(k); else next.add(k);
                                                        setSelected(next);
                                                    }}
                                                />
                                            </td>
                                        )}
                                        {visibleColumns.map((c) => {
                                            const value = valueOf(c, row);
                                            const content = c.cell ? c.cell(row, value, absoluteIndex) : formatValue(c, value, row);
                                            const cls = [
                                                c.align === 'end' ? 'align-end is-num' : c.align === 'center' ? 'align-center' : '',
                                                c.nowrap === false ? '' : 'is-nowrap',
                                                typeof c.className === 'function' ? c.className(row) : (c.className || ''),
                                            ].join(' ');
                                            return <td key={c.key} className={cls}>{content}</td>;
                                        })}
                                    </tr>
                                    {renderExpanded && isExpanded && (
                                        <tr className="ds-table__expanded">
                                            <td colSpan={colSpan}>{renderExpanded(row)}</td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );

    const content = (
        <>
            {(title || description || headerActions) && (
                <div className="ds-table-card__head">
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {title && <div className="ds-table-card__title">{title}</div>}
                        {description && <div className="ds-table-card__desc">{description}</div>}
                    </div>
                    {headerActions}
                </div>
            )}

            {showToolbar && (
                <div className="ds-table-toolbar">
                    {searchable && (
                        <div className="ds-search">
                            <Icon glyph={ICONS.search} size="sm" />
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={searchPlaceholder}
                                aria-label={searchPlaceholder}
                                id={`${uid}-search`}
                            />
                        </div>
                    )}
                    {toolbar}
                    <span className="ds-table-toolbar__spacer" />
                    {actions}
                    {columnToggle && (
                        <Popover
                            label="Table view options"
                            width={220}
                            trigger={({ props }) => (
                                <Button {...props} variant="ghost" size="md" leadingIcon={<Icon glyph={ICONS.filter} size="sm" />}>
                                    View
                                </Button>
                            )}
                        >
                            <div className="ds-menu-heading">Density</div>
                            {['compact', 'default', 'comfortable'].map((d) => (
                                <label key={d} className="ds-menu-check">
                                    <input type="radio" name={`${uid}-density`} checked={density === d} onChange={() => setDensity(d)} />
                                    {d.charAt(0).toUpperCase() + d.slice(1)}
                                </label>
                            ))}
                            <div className="ds-menu-divider" />
                            <div className="ds-menu-heading">Columns</div>
                            {columns.filter((c) => c.hideable !== false).map((c) => (
                                <label key={c.key} className="ds-menu-check">
                                    <input
                                        type="checkbox"
                                        checked={!hidden.has(c.key)}
                                        onChange={() => {
                                            const next = new Set(hidden);
                                            if (next.has(c.key)) next.delete(c.key); else next.add(c.key);
                                            if (next.size < columns.length) setHidden(next);
                                        }}
                                    />
                                    {typeof c.header === 'string' ? c.header : c.key}
                                </label>
                            ))}
                        </Popover>
                    )}
                    {canExport && (
                        <Button
                            variant="secondary"
                            leadingIcon={<Icon glyph={ICONS.download} size="sm" />}
                            onClick={() => exportRows(processed)}
                            isLoading={exporting}
                            disabled={processed.length === 0}
                        >
                            Export
                        </Button>
                    )}
                </div>
            )}

            {filters && <div className="ds-table-filters">{filters}</div>}

            {table}

            {selectable && selectedRows.length > 0 && (
                <div className="ds-bulkbar" role="region" aria-label="Bulk actions">
                    <span className="ds-bulkbar__count">{selectedRows.length} selected</span>
                    {canExport && (
                        <Button size="sm" variant="secondary" onClick={() => exportRows(selectedRows)}>Export selected</Button>
                    )}
                    {bulkActions && bulkActions(selectedRows, clearSelection)}
                    <Button size="sm" variant="ghost" onClick={clearSelection} aria-label="Clear selection">
                        <Icon glyph={ICONS.close} size="xs" />
                    </Button>
                </div>
            )}

            {(pagination || footer) && (
                <div className="ds-table-footer">
                    {pagination && (
                        <span aria-live="polite">
                            {totalRows === 0 ? 'No rows' : `${NUMBER.format(firstShown)}–${NUMBER.format(lastShown)} of ${NUMBER.format(totalRows)}`}
                        </span>
                    )}
                    {footer}
                    <span className="ds-table-footer__spacer" />
                    {pagination && totalRows > Math.min(...pageSizeOptions) && (
                        <label className="flex items-center gap-2">
                            <span>Rows</span>
                            <select
                                className="ds-page-size"
                                value={pageSize}
                                onChange={(e) => {
                                    const n = Number(e.target.value);
                                    setPageSize(n);
                                    if (onPageSizeChange) onPageSizeChange(n);
                                }}
                            >
                                {pageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </label>
                    )}
                    {pagination && pageCount > 1 && (
                        <nav className="ds-pager" aria-label="Pagination">
                            <button type="button" onClick={() => setPage(safePage - 1)} disabled={safePage <= 1} aria-label="Previous page">
                                <Icon glyph={ICONS.chevronLeft} size="xs" />
                            </button>
                            {pageList(safePage, pageCount).map((p, i) => (
                                p === '…'
                                    ? <span key={`gap-${i}`} style={{ padding: '0 4px' }}>…</span>
                                    : (
                                        <button key={p} type="button" onClick={() => setPage(p)} aria-current={p === safePage ? 'page' : undefined}>
                                            {p}
                                        </button>
                                    )
                            ))}
                            <button type="button" onClick={() => setPage(safePage + 1)} disabled={safePage >= pageCount} aria-label="Next page">
                                <Icon glyph={ICONS.chevronRight} size="xs" />
                            </button>
                        </nav>
                    )}
                </div>
            )}
        </>
    );

    if (!card) return <div className={className}>{content}</div>;
    return <div className={`ds-table-card ${className}`}>{content}</div>;
}
